// 개발용 — Agent 서버 stream/resume 요청과 응답 SSE 이벤트를 시간순으로
// 보여주는 floating drawer. 사용 끝나면 본 파일 + TaskEditorPage 마운트 +
// debugLog + apiClient capture 만 제거하면 끝.

import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Box, Button, Chip, Sheet, Stack, Typography } from "@mui/joy";
import { isDev } from "config";
import { theme } from "styles/theme";
import {
  clearDebug,
  getDebugEntries,
  subscribeDebug,
  type DebugEntry,
  type DebugEvent,
} from "./debugLog";
import type { SSEEvent } from "./types";
import { taskEditorScrollbarSx } from "../utils/taskEditorScrollbarSx";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

function useDebugEntries(): DebugEntry[] {
  return useSyncExternalStore(subscribeDebug, getDebugEntries, getDebugEntries);
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(
    d.getMilliseconds()
  ).padStart(3, "0")}`;
}

function formatDeltaMs(ms: number): string {
  if (ms < 1000) return `+${ms}ms`;
  return `+${(ms / 1000).toFixed(2)}s`;
}

const STATUS_COLOR: Record<
  DebugEntry["status"],
  "primary" | "success" | "danger"
> = {
  streaming: "primary",
  done: "success",
  error: "danger",
};

const EVENT_COLOR: Record<
  SSEEvent["event"],
  "primary" | "neutral" | "warning" | "success" | "danger"
> = {
  session: "neutral",
  message_chunk: "neutral",
  reasoning: "neutral",
  tool_call: "primary",
  tool_result: "primary",
  todo_update: "neutral",
  pending_approval: "warning",
  task_patch: "warning",
  final: "success",
  debug: "neutral",
  error: "danger",
  keepalive: "neutral",
};

export default function AgentDebugPanel() {
  const [open, setOpen] = useState(false);
  const entries = useDebugEntries();

  if (!isDev) return null;

  return (
    <>
      <Button
        size="sm"
        variant="solid"
        color="neutral"
        onClick={() => setOpen((v) => !v)}
        sx={{
          position: "fixed",
          right: "16px",
          bottom: "16px",
          zIndex: 9998,
          fontFamily: MONO,
          fontSize: "11px",
        }}
      >
        🐛 agent log {entries.length > 0 && `(${entries.length})`}
      </Button>

      {open && (
        <Sheet
          variant="outlined"
          sx={{
            position: "fixed",
            right: "16px",
            bottom: "60px",
            width: "min(960px, calc(100vw - 32px))",
            height: "min(85vh, calc(100vh - 80px))",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            overflow: "hidden",
            backgroundColor: theme.vars.palette.Background.Level1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: "8px 12px",
              borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
            }}
          >
            <Typography level="title-sm">Agent SSE 히스토리</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={clearDebug}
                sx={{ fontSize: "11px" }}
              >
                지우기
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
                sx={{ fontSize: "11px" }}
              >
                닫기
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ overflow: "auto", flex: 1, ...taskEditorScrollbarSx }}>
            {entries.length === 0 ? (
              <Box
                sx={{
                  p: "20px",
                  textAlign: "center",
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "12px",
                }}
              >
                아직 보낸 요청 없음
              </Box>
            ) : (
              entries.map((entry) => (
                <DebugEntryRow key={entry.id} entry={entry} />
              ))
            )}
          </Box>
        </Sheet>
      )}
    </>
  );
}

function DebugEntryRow({ entry }: { entry: DebugEntry }) {
  const [expanded, setExpanded] = useState(false);
  const isStream = entry.kind === "stream";
  const summary =
    entry.kind === "stream"
      ? entry.body.message.slice(0, 60) +
        (entry.body.message.length > 60 ? "…" : "")
      : `decisions: ${entry.body.decisions.map((d) => d.type).join(", ")}`;
  const hasTask = entry.body.current_task != null;
  const eventCount = entry.events.length;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          p: "8px 12px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.vars.palette.Background.Level2,
          },
        }}
      >
        <Chip
          size="sm"
          color={isStream ? "primary" : "warning"}
          variant="soft"
          sx={{ fontFamily: MONO, fontSize: "10px", minWidth: "60px" }}
        >
          {entry.kind}
        </Chip>
        <Typography
          level="body-xs"
          sx={{ fontFamily: MONO, color: theme.vars.palette.Content.Weak }}
        >
          {formatTime(entry.at)}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary}
        </Typography>
        {hasTask && (
          <Chip size="sm" variant="outlined" sx={{ fontSize: "10px" }}>
            task
          </Chip>
        )}
        <Chip
          size="sm"
          color={STATUS_COLOR[entry.status]}
          variant="soft"
          sx={{ fontFamily: MONO, fontSize: "10px" }}
        >
          {entry.status === "streaming"
            ? `↻ ${eventCount}`
            : entry.status === "error"
              ? `× ${eventCount}`
              : `✓ ${eventCount}`}
        </Chip>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {expanded ? "▾" : "▸"}
        </Typography>
      </Stack>
      {expanded && (
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Background.Level2,
            borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
          }}
        >
          <SectionHeader label="REQUEST" />
          <CopyableJson value={entry.body} />
          <SectionHeader
            label={`RESPONSE — ${eventCount} 이벤트 (${entry.status})${
              entry.error ? ` · ${entry.error}` : ""
            }`}
          />
          {entry.events.length === 0 ? (
            <Box
              sx={{
                p: "10px 12px",
                fontSize: "11px",
                color: theme.vars.palette.Content.Weak,
              }}
            >
              아직 이벤트 없음
            </Box>
          ) : (
            entry.events.map((ev, i) => (
              <EventRow key={i} event={ev} startAt={entry.at} />
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <Box
      sx={{
        p: "4px 12px",
        fontSize: "9px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        color: theme.vars.palette.Content.Weak,
        backgroundColor: theme.vars.palette.Background.Level1,
        borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
        borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
      }}
    >
      {label}
    </Box>
  );
}

function EventRow({
  event: ev,
  startAt,
}: {
  event: DebugEvent;
  startAt: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const name = ev.event.event;
  const summary = summarizeEvent(ev.event);
  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          p: "5px 12px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.vars.palette.Background.Level1,
          },
        }}
      >
        <Chip
          size="sm"
          variant="soft"
          color={EVENT_COLOR[name]}
          sx={{ fontFamily: MONO, fontSize: "10px", minWidth: "120px" }}
        >
          {name}
        </Chip>
        <Typography
          level="body-xs"
          sx={{
            fontFamily: MONO,
            color: theme.vars.palette.Content.Weak,
            minWidth: "60px",
          }}
        >
          {formatDeltaMs(ev.at - startAt)}
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: theme.vars.palette.Content.Medium,
          }}
        >
          {summary}
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {expanded ? "▾" : "▸"}
        </Typography>
      </Stack>
      {expanded && <CopyableJson value={ev.event} />}
    </Box>
  );
}

function summarizeEvent(event: SSEEvent): string {
  switch (event.event) {
    case "message_chunk":
      return JSON.stringify(event.data.delta);
    case "tool_call":
      return `${event.data.name}(${Object.keys(event.data.args).join(", ")})`;
    case "tool_result":
      return `${event.data.name} → ${event.data.preview.slice(0, 80)}`;
    case "todo_update":
      return `${event.data.todos.length} todos`;
    case "pending_approval":
      return event.data.action_requests.map((a) => a.name).join(", ");
    case "task_patch":
      return `${event.data.task.content.blocks.length} blocks`;
    case "final":
      return `task_changed=${event.data.task_changed}`;
    case "debug":
      return `${event.data.execution_time_ms}ms${
        event.data.trace_id ? ` · ${event.data.trace_id}` : ""
      }`;
    case "error":
      return `${event.data.type}: ${event.data.message}`;
    case "session":
      return event.data.thread_id;
    default:
      return "";
  }
}

function CopyableJson({ value }: { value: unknown }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(value, null, 2);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const handleCopy = () => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard?.writeText(json).then(() => setCopied(true));
  };
  return (
    <Box sx={{ position: "relative" }}>
      <Button
        size="sm"
        variant="plain"
        color="neutral"
        onClick={handleCopy}
        sx={{
          position: "absolute",
          right: "6px",
          top: "6px",
          fontSize: "10px",
          zIndex: 1,
        }}
      >
        {copied ? "복사됨" : "복사"}
      </Button>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: "10px 12px",
          maxHeight: "480px",
          overflow: "auto",
          fontFamily: MONO,
          fontSize: "11px",
          lineHeight: 1.5,
          whiteSpace: "pre",
          color: theme.vars.palette.Content.Strong,
          ...taskEditorScrollbarSx,
        }}
      >
        {json}
      </Box>
    </Box>
  );
}
